import i18n from '@/utils/i18n';
import React, { ReactNode, RefObject, useState } from 'react';
import { Badge, Typography, View, YStack } from '@usepepper/sprout-kit-ui';
import {
  createColumnHelper,
  flexRender,
  HeaderGroup,
  Row,
} from '@tanstack/react-table';
import {
  CustomColumnDef,
  TableScrollHandle,
} from '@experiences/components/table/types';
import { FinanceHubTableTooltipTextCell } from '@finance-hub/components/finance-hub-table-tooltip-text-cell';
import { InvoiceListRecordType } from '@/types';
import { LayoutChangeEvent, Platform, ViewStyle } from 'react-native';
import { Table } from '@experiences/components/table/table';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';
import {
  UNPAID_INVOICES_TABLE_COLUMNS,
  UNPAID_INVOICES_TABLE_ROW_HEIGHT,
} from '@finance-hub/domain/invoicing/components/unpaid-invoices-table-columns';
import {
  UnpaidInvoiceRow,
  UnpaidInvoiceSort,
  UnpaidInvoiceSortField,
} from '@finance-hub/domain/invoicing/blocs/types/unpaid-invoice-list';
import { UnpaidInvoicesTableRow } from '@finance-hub/domain/invoicing/components/unpaid-invoices-table-row';
import { useTheme } from '@/contexts/theme-context';

const columnHelper = createColumnHelper<UnpaidInvoiceRow>();

const BODY_CONTAINER_STYLE = Platform.select<ViewStyle>({
  web: { width: '100%' },
});

const renderDaysOverdueCell = (row: UnpaidInvoiceRow) => {
  if (row.recordType === InvoiceListRecordType.CreditMemo) {
    return (
      <Typography bold color="$textSecondary" size="$xs">
        —
      </Typography>
    );
  }

  if (row.daysOverdue > 0) {
    return (
      <Badge color="warning" size="$sm" variant="outline">
        {i18n.t('finance_hub.invoices.table.days_overdue', {
          count: row.daysOverdue,
        })}
      </Badge>
    );
  }

  return (
    <Badge color="success" size="$sm" variant="outline">
      {i18n.t('finance_hub.invoices.table.current')}
    </Badge>
  );
};

const CELL_RENDERERS: Record<
  UnpaidInvoiceSortField,
  (row: UnpaidInvoiceRow) => ReactNode
> = {
  customerName: (row) => (
    <FinanceHubTableTooltipTextCell
      fontWeight="$medium"
      size="$sm"
      text={row.customerName}
    />
  ),
  daysOverdue: renderDaysOverdueCell,
  dueDate: (row) => (
    <Typography color="$textSecondary" size="$sm">
      {row.dueDateLabel}
    </Typography>
  ),
  erpLastSyncedAt: (row) =>
    row.erpLastSyncedDateLabel && row.erpLastSyncedTimeLabel ? (
      <YStack gap="$2">
        <Typography color="$textSecondary" size="$xs">
          {row.erpLastSyncedDateLabel}
        </Typography>
        <Typography color="$textSecondary" size="$2xs">
          {row.erpLastSyncedTimeLabel}
        </Typography>
      </YStack>
    ) : null,
  invoiceNumber: (row) => (
    <FinanceHubTableTooltipTextCell bold size="$sm" text={row.invoiceNumber} />
  ),
  remainingBalance: (row) => (
    <YStack gap="$2">
      <Typography
        bold
        color={
          row.recordType === InvoiceListRecordType.CreditMemo
            ? '$textSuccess'
            : '$textPrimary'
        }
        size="$sm"
      >
        {row.remainingBalanceLabel}
      </Typography>
      {row.amountPaidLabel ? (
        <Typography color="$textSuccess" size="$xs">
          {row.amountPaidLabel}
        </Typography>
      ) : null}
    </YStack>
  ),
};

const COLUMN_DEFS: CustomColumnDef<UnpaidInvoiceRow>[] =
  UNPAID_INVOICES_TABLE_COLUMNS.map((column) =>
    columnHelper.display({
      cell: (info) => CELL_RENDERERS[column.sortField](info.row.original),
      header: () => i18n.t(column.labelKey),
      id: column.sortField,
      meta: { initialPercentWidth: column.initialPercentWidth },
      ...(column.minSize ? { minSize: column.minSize } : {}),
    }),
  );

const renderRow = ({ row }: { row: Row<UnpaidInvoiceRow> }) => (
  <UnpaidInvoicesTableRow key={row.id} row={row} />
);

interface Props {
  emptyComponent: ReactNode;
  listFooter?: ReactNode;
  onColumnPress: (field: UnpaidInvoiceSortField) => void;
  onEndReached?: () => void;
  rows: UnpaidInvoiceRow[];
  scrollHandleRef?: RefObject<TableScrollHandle | null>;
  sort: UnpaidInvoiceSort;
}

export const UnpaidInvoicesTable: React.FC<Props> = ({
  emptyComponent,
  listFooter,
  onColumnPress,
  onEndReached,
  rows,
  scrollHandleRef,
  sort,
}) => {
  const theme = useTheme();
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null);

  const handleLayout = (event: LayoutChangeEvent) => {
    setLayoutWidth(event.nativeEvent.layout.width);
  };

  const renderHeaderRow = ({
    headerGroup,
  }: {
    headerGroup: HeaderGroup<UnpaidInvoiceRow>;
  }) => (
    <TableHeaderRow key={headerGroup.id}>
      {headerGroup.headers.map((header) => (
        <TableHeaderCell
          columnId={header.id}
          key={header.id}
          onPress={() => onColumnPress(header.id as UnpaidInvoiceSortField)}
          sortDirection={sort.field === header.id ? sort.direction : undefined}
        >
          <Typography
            bold
            color="$textSecondary"
            size="$xs"
            textTransform="uppercase"
          >
            <>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </>
          </Typography>
        </TableHeaderCell>
      ))}
    </TableHeaderRow>
  );

  if (!layoutWidth) {
    return (
      <View
        accessibilityRole="progressbar"
        accessibilityState={{ busy: true }}
        accessible
        flex={1}
        onLayout={handleLayout}
      />
    );
  }

  return (
    <View flex={1} onLayout={handleLayout}>
      <Table
        bodyContainerStyle={BODY_CONTAINER_STYLE}
        cellPadding={theme.sk.spacing.spaceSmall}
        columns={COLUMN_DEFS}
        data={rows}
        emptyComponent={emptyComponent}
        estimatedRowHeight={UNPAID_INVOICES_TABLE_ROW_HEIGHT}
        headerPadding={theme.sk.spacing.spaceSmall}
        isActive
        layoutWidth={layoutWidth}
        listFooter={listFooter}
        onEndReached={onEndReached}
        renderHeaderRow={renderHeaderRow}
        renderRow={renderRow}
        scrollHandleRef={scrollHandleRef}
      />
    </View>
  );
};
